import { MULTIPLE_NUMBER_EVENT_REGEX, MULTIPLE_STRING_REGEX } from "src/constants/constants";
import { transformDateTime } from "src/lib/date-time.util";
import { dateConverstion, nullFilterField } from "src/utils/utils";

// ─── Cascade helpers (shared by renderGroupReports + advanceQueryFilter) ──────────────────
export const flatId = (v) => (v != null && typeof v === "object" ? (v.id ?? v) : v);

export const isSelectAllId = (id) => id === 0 || id === "0" || id === "Select All";

export const isSelectAllSelection = (val) =>
  Array.isArray(val) && val.length > 0 && val.every((v) => isSelectAllId(flatId(v)));

export const addToCascadeContext = (ctx, field, currentVal, apiResult) => {
  // Unwrap {firstKey, mappedData} shape (specialty/group/provider) vs plain key (comp_plan/period).
  const firstKey =
    apiResult != null && typeof apiResult === "object" && "firstKey" in apiResult ? apiResult.firstKey : apiResult;

  if (isSelectAllSelection(currentVal)) {
    // Select All means "no filter" — don't restrict downstream calls; skip.
    return;
  }

  const hasCurrentVal =
    currentVal != null && currentVal !== "" && !(Array.isArray(currentVal) && currentVal.length === 0);

  if (hasCurrentVal) {
    // User has a real previous selection — always prefer it over the first-item fallback.
    // For rich results (specialty/group/provider) validate against the returned list;
    // for plain-key results (comp_plan/period) trust it — useEffect will correct if stale.
    if (apiResult != null && typeof apiResult === "object" && "mappedData" in apiResult) {
      const { mappedData = [] } = apiResult;
      const fetchedIdSet = new Set(mappedData.map((d) => String(d.id)));
      if (Array.isArray(currentVal)) {
        // Keep all still-valid selections (partial intersection), not just check if ALL are valid.
        const stillValidItems =
          fetchedIdSet.size > 0
            ? currentVal.filter((v) => {
                const id = String(flatId(v));
                return id === "0" || fetchedIdSet.has(id);
              })
            : [];
        if (stillValidItems.length > 0) {
          ctx[field] = stillValidItems;
        } else if (firstKey != null && !isSelectAllId(firstKey)) {
          ctx[field] = firstKey;
        } else {
          delete ctx[field];
        }
      } else {
        const singleId = String(flatId(currentVal));
        const isStillValid = fetchedIdSet.size > 0 && (singleId === "0" || fetchedIdSet.has(singleId));
        ctx[field] = isStillValid ? currentVal : firstKey != null && !isSelectAllId(firstKey) ? firstKey : undefined;
        if (ctx[field] === undefined) delete ctx[field];
      }
    } else {
      // Plain key result — no mappedData to validate against; use firstKey as safe default.
      if (firstKey != null && !isSelectAllId(firstKey)) {
        ctx[field] = firstKey;
      }
    }
  } else if (firstKey != null && !isSelectAllId(firstKey)) {
    // No previous selection — fall back to the first item.
    ctx[field] = firstKey;
  }
};
// ─────────────────────────────────────────────────────────────────────────────

export const convertDate = (date) => {
  return transformDateTime({
    date: date,
    format: "YYYY-MM-DD",
  });
};

export const getOperatorList = (type, operatorList, value) => {
  if (!operatorList) return [];

  const map = {
    string: operatorList.stringOperator || [],
    boolean: operatorList.stringOperator || [],
    number: operatorList.numericOperator || [],
    date: operatorList.dateOperator || [],
  };

  let operators = map[type];
  if (value?.isRequired) {
    operators = operators.filter((x) => x.operator_value !== "IS NULL" && x.operator_value !== "IS NOT NULL");
  }
  if (value?.dropDownControl) {
    operators = operators.filter((x) => x.operator_value !== "LIKE" && x.operator_value !== "NOT LIKE");
  }

  if (value?.allowedOperators?.length) {
    operators = operators.filter((x) => value.allowedOperators.includes(x.operator_value));
  }

  return operators;
};

export const disableFields = (operator) => {
  return (
    operator?.toLowerCase().replace(/\s/g, "") === "isempty" ||
    operator?.toLowerCase().replace(/\s/g, "") === "isnotempty"
  );
};

export const createEmptyGroup = () => ({
  id: Date.now() + Math.random(),
  logic: "and",
  items: [],
  value: { logic: "and" },
});

export const createEmptyExpression = () => ({
  id: Date.now() + Math.random(),
  value: null,
  operator: { operator: "eq" },
  inputValue: { field: "", value: "" },
});

const normalizeLogic = (g) => {
  if (!g) return "and";
  if (g.filters?.length === 1 && g.filters[0]?.filters?.length) {
    return g.filters[0].logic || "and";
  }
  return g.logic || "and";
};

export const collectFilters = (group, options = {}) => {
  if (!group) return null;
  const { isAdvanceFilter = false } = options;

  const logic = normalizeLogic(group);

  const resolveValue = (item) => {
    const inputValue = item.inputValue?.value;
    const field = item.value.field;
    const dropDown = item?.value?.dropDownControl;
    const isApproval = field === "approval_status_mstr_key";
    const isDate = item.value.type === "date";
    const isEventDatasource = field === "event_data_source";
    const isServiceLocation = field === "service_location";

    if (item.value.type === "boolean") {
      return inputValue?.id || false;
    }

    // Handle arrays
    if (Array.isArray(inputValue)) {
      if (inputValue.length === 0) return inputValue;

      const isValidArray = inputValue.every((val) => val && typeof val === "object" && "text" in val && "id" in val);
      if (isValidArray) {
        if (isServiceLocation || isEventDatasource) {
          if (inputValue[0]?.id.toString() !== "0") {
            return inputValue.map((val) => `"${val.id || val}"`);
          }
        }
        if (isApproval || dropDown)
          return inputValue.map((val) => {
            const id = val.id ?? val;
            return isSelectAllId(id) ? "0" : id;
          });
        return inputValue.map((val) => val.text ?? val);
      }

      if ((isServiceLocation || isEventDatasource) && ["IN", "NOT IN"].includes(item.operator.operator_value)) {
        return inputValue.map((val) => `"${val.id || val}"`);
      } else {
        // Required for report advance filter: preserve raw primitive ID arrays and normalise Select All.
        if (isAdvanceFilter && dropDown) {
          return inputValue.map((val) => (isSelectAllId(val) ? "0" : val));
        }
        return inputValue.filter((x) => x !== "").length === 0 ? "" : inputValue;
      }
    }

    // Handle objects
    if (typeof inputValue === "object" && inputValue !== null) {
      if (isDate) {
        if (["BETWEEN", "NOT BETWEEN"].includes(item.operator.operator_value)) {
          return {
            start: inputValue?.start ? convertDate(inputValue.start) : "",
            end: inputValue?.end ? convertDate(inputValue.end) : "",
          };
        }
        return convertDate(inputValue);
      }
      if (dropDown || isApproval) {
        const id = inputValue.id ?? "";
        return id === "Select All" ? "0" : id;
      }
      return inputValue.id?.toString() ?? "";
    }

    // Handle primitive values
    if (isDate) return convertDate(inputValue);
    return inputValue;
  };
  return {
    logic,
    filters: (group.items || [])
      .map((item) => {
        if (item.items) return collectFilters(item, options); // nested group

        const hasField = item?.value?.field;
        const operatorLabel = item?.operator?.operator_label;
        if (!hasField || !operatorLabel) return null;

        // Handle IS EMPTY / IS NOT EMPTY
        if (["IS EMPTY", "IS NOT EMPTY"].includes(operatorLabel)) {
          return {
            field: item.value.field,
            operator: operatorLabel,
            value: "",
            dataItemKey: Array.isArray(item?.inputValue?.value)
              ? item?.inputValue?.value?.[0]?.dataItemKey ||
                item?.inputValue?.value?.dataItemKey ||
                item?.inputValue?.dataItemKey ||
                ""
              : item?.inputValue?.value?.dataItemKey || item?.inputValue?.dataItemKey || "",
            operatorValue: item.operator.operator_value,
            dropDownControl: item?.value?.dropDownControl || false,
            alias: item?.value?.alias,
            type: item?.value?.type,
          };
        }
        // Normal case
        return {
          field: item.value.field,
          operator: operatorLabel,
          value: resolveValue(item),
          dataItemKey: Array.isArray(item?.inputValue?.value)
            ? item?.inputValue?.value?.[0]?.dataItemKey ||
              item?.inputValue?.value?.dataItemKey ||
              item?.inputValue?.dataItemKey ||
              ""
            : item?.inputValue?.value?.dataItemKey || item?.inputValue?.dataItemKey || "",
          operatorValue: item.operator.operator_value,
          dropDownControl: item?.value?.dropDownControl || false,
          gname: item?.value?.gname || "",
          alias: item?.value?.alias,
          type: item?.value?.type,
        };
      })
      .filter(Boolean),
  };
};

export const getDefaultValueForField = (fieldType, dataItemKey, operator, dropDownControl) => {
  switch (fieldType) {
    case "date":
      return null;
    case "number":
      return "";
    case "boolean":
      return BOOLEAN_OPTIONS[0];
    case "string":
      if (operator === "IN" || operator === "NOT IN") {
        return dropDownControl && operator === "IN"
          ? [
              {
                text: "Select All",
                id: 0,
                dataItemKey: dataItemKey,
              },
            ]
          : "";
      } else {
        return "";
      }
    default:
      return "";
  }
};

export const normalizeFilterGroups = (filters, maxFields = 10, defaultLogic = "and") => {
  let tempArray = [];
  const buildFilterString = (group) => {
    const logic = (group.logic || "AND").toUpperCase();
    const parts = [];

    for (const item of group.filters) {
      if (item.filters && Array.isArray(item.filters)) {
        const nested = buildFilterString(item);
        if (nested) {
          tempArray.push(`(${nested})`);
        }
      } else {
        tempArray.push(item);
      }
    }
  };

  const group = Array.isArray(filters) ? filters[0] : filters;
  const result = buildFilterString(group);

  return tempArray;
};

export const renderTooltip = (filterObj, gridColumns, fieldConfigMap) => {
  if (!filterObj) return "";

  const processFilter = (obj, level = 0) => {
    if (Array.isArray(obj.filters)) {
      const parts = [];

      obj.filters.forEach((child, index) => {
        const childStr = processFilter(child, level + 1);
        if (!childStr) return;

        if (index === 0) {
          // First item, no logic before it
          parts.push(childStr);
        } else {
          // If child has its own logic, use it; else use parent's
          const joinLogic = (child.logic || obj.logic || "AND").toUpperCase();
          parts.push(`${joinLogic} ${childStr}`);
        }
      });

      const groupStr = parts.join(" ");
      return level > 0 ? `(${groupStr})` : groupStr;
    }

    // Single condition handling
    const findColumnType = gridColumns.find((x) => x.field === obj.field);

    const listData = fieldConfigMap?.[obj.field];
    if (listData) {
      if (Array.isArray(obj.value) && obj?.value?.[0] !== 0) {
        // Add each selected id to its display text. "Select All" 
        const labels = obj.value.map(String).map((val) => {
          if (isSelectAllId(val)) return "Select All";
          const option = (listData.data || []).find((x) => x.id?.toString() === val);
          return option ? option.text : val;
        });
        return `${findColumnType?.title || obj.field} ${obj.operatorValue || obj.operator} ${labels.join(", ")}`;
      } else {
        const match = listData.data.find((item) => item.id === obj.value);
        const value = match ? match.text : obj.value;
        let convertValue = value.length > 0 ? (value?.[0] === 0 ? "Select All" : value) : value;
        return `${findColumnType?.title || obj.field} ${obj.operatorValue || obj.operator} ${convertValue}`;
      }
    }

    if (!findColumnType) {
      return `${obj.field} ${obj.operatorValue || obj.operator} ${obj.value}`;
    }

    if (findColumnType.type === "date") {
      const operator = obj.operator.toLowerCase().trim();
      if (operator === "between") {
        return `${findColumnType.title} Range ${convertDate(obj.value.start)} to ${convertDate(obj.value.end)}`;
      } else if (operator === "not between") {
        return `${findColumnType.title} Not Range ${convertDate(obj.value.start)} to ${convertDate(obj.value.end)}`;
      }
      return `${findColumnType.title} ${obj.operatorValue || obj.operator} ${convertDate(obj.value)}`;
    }

    return `${findColumnType.title || obj.field} ${obj.operatorValue || obj.operator} ${obj.value}`;
  };
  return processFilter(filterObj);
};

export const handleRenderValidation = (val, item) => {
  const { key, operator_value, field, dropDownControl } = item;

  const isNullOp = operator_value === "IS NULL" || operator_value === "IS NOT NULL";
  const isInOp = operator_value === "IN" || operator_value === "NOT IN";
  const isNotInOp = operator_value === "NOT IN";

  switch (key) {
    case "number": {
      if (!isNullOp) {
        if ((val === "" || isNaN(val)) && !isInOp) {
          return { error: true, errorMsg: "Value is required" };
        }
        if (!val && isInOp) {
          return { error: true, errorMsg: "Insert multiple numbers required." };
        } else if (!MULTIPLE_NUMBER_EVENT_REGEX.test(val) && isInOp) {
          return { error: true, errorMsg: "Invalid multiple numbers." };
        }
      }
      return { error: false, errorMsg: "" };
    }

    case "string": {
      if (!isNullOp) {
        if (!val && isInOp && !dropDownControl) {
          return { error: true, errorMsg: "Insert multiple string required." };
        } else if (!MULTIPLE_STRING_REGEX.test(val) && isInOp && !dropDownControl) {
          return { error: true, errorMsg: "Invalid multiple string." };
        }

        if (isNotInOp && dropDownControl && (!val || val.length === 0)) {
          return { error: true, errorMsg: "Please select an option." };
        }
        if (dropDownControl && Array.isArray(val) && val.length === 0) {
          return { error: true, errorMsg: "Value is required" };
        }
        if (
          dropDownControl &&
          val &&
          typeof val === "object" &&
          !Array.isArray(val) &&
          (val.id === undefined || val.id === null || val.id === "")
        ) {
          return { error: true, errorMsg: "Value is required" };
        }
        if (!val) {
          return { error: true, errorMsg: "Value is required" };
        }
      }

      return { error: false, errorMsg: "" };
    }

    case "date": {
      if (operator_value === "BETWEEN" || operator_value === "NOT BETWEEN") {
        if (!val || !val.start || !val.end) {
          return { error: true, errorMsg: "Start date and end date are required." };
        }
        if (new Date(val.start) > new Date(val.end)) {
          return { error: true, errorMsg: "Start date cannot be greater than end date." };
        }
      }
      if (!val && !isNullOp) {
        return { error: true, errorMsg: "Value is required" };
      }
      return { error: false, errorMsg: "" };
    }

    default:
      return { error: false, errorMsg: "" };
  }
};

export const BOOLEAN_OPTIONS = [
  { text: "Is true", id: true },
  { text: "Is false", id: false },
];

const convertFilterClauseForQueryBuilder = (
  fieldName,
  operatorStr,
  valueStr,
  fieldType = "string",
  dropDownControl
) => {
  let returnVal = valueStr;
  const quoteOp = [
    "Between",
    "Not Between",
    "Is Equal To",
    "Is Not Equal To",
    "Is Greater Than",
    "Is Less Than",
    "Is Greater Or Equal To",
    "Is Less Or Equal To",
    "IN",
    "NOT IN",
  ];
  const quoteType = ["string", "date", "text"];

  if (operatorStr !== "In" && operatorStr !== "Not In") {
    if (fieldType === "string" && typeof valueStr === "string" && valueStr) {
      returnVal = valueStr.addslashes();
    }
  }

  if (fieldType === "text" && typeof valueStr === "string" && valueStr) {
    returnVal = valueStr.addslashes();
  }

  let datesObj = {};
  if (fieldType === "date") {
    if (typeof valueStr === "object" && (operatorStr === "Between" || operatorStr === "Not Between")) {
      let startDate = dateConverstion(valueStr.start);
      let endDate = dateConverstion(valueStr.end);
      datesObj = { start: startDate, end: endDate };
    } else {
      returnVal = dateConverstion(valueStr);
    }
  }

  if (fieldType === "string") {
    if (dropDownControl) {
      if (fieldName?.replace(/^a\./, "")) {
        returnVal =
          typeof returnVal === "number"
            ? returnVal
            : operatorStr === "IN" || operatorStr === "NOT IN"
              ? `'${returnVal.addslashes()}'`
              : returnVal;
      }
    }
  } else if (quoteOp.indexOf(operatorStr) > -1 && quoteType.indexOf(fieldType) > -1) {
    returnVal = "'" + (operatorStr === "string" ? returnVal.convertYmdDate() : returnVal) + "'";
  }
  if (fieldType !== "date" && fieldType !== "number") {
    switch (operatorStr) {
      case "Is Equal To":
        return fieldName + " = " + returnVal;
      case "Is Not Equal To":
        return nullFilterField(fieldName, fieldType) + " <> " + returnVal;
      case "Does Not Contain":
        return nullFilterField(fieldName) + " NOT LIKE '%" + returnVal + "%'";
      case "In":
        return dropDownControl ? fieldName + " IN (" + returnVal + ")" : fieldName + " IN" + returnVal;
      case "Not In":
        return dropDownControl ? " NOT IN (" + returnVal + ")" : fieldName + " NOT IN" + returnVal;
      case "Contains":
        return fieldName + " LIKE '%" + returnVal + "%'";
      case "startswith":
        return fieldName + " LIKE '" + returnVal + "%'";
      case "endswith":
        return fieldName + " LIKE '%" + returnVal + "'";
      case "Is Greater Than":
        return nullFilterField(fieldName, fieldType) + " > " + returnVal;
      case "Is Less Than":
        return nullFilterField(fieldName, fieldType) + " < " + returnVal;
      case "Is Greater Or Equal To":
        return nullFilterField(fieldName, fieldType) + " >= " + returnVal;
      case "Is Less Or Equal To":
        return nullFilterField(fieldName, fieldType) + " <= " + returnVal;
      case "Is Empty":
        return nullFilterField(fieldName, fieldType) + " = ''";
      case "Is Not Empty":
        return nullFilterField(fieldName, fieldType) + " <> ''";
      default:
        throw new Error("operator not defined in converter method");
    }
  }

  // Add condition for check null and nullable value
  if (fieldType === "number") {
    switch (operatorStr) {
      case "Is Equal To":
        return fieldName + " = " + returnVal;
      case "Is Not Equal To":
        return nullFilterField(fieldName, fieldType) + " <> " + returnVal;
      case "In":
        return fieldName + " IN" + returnVal;
      case "Not In":
        return fieldName + " NOT IN" + returnVal;
      case "Does Not Contain":
        return nullFilterField(fieldName) + " NOT LIKE '%" + returnVal + "%'";
      case "Contains":
        return fieldName + " LIKE '%" + returnVal + "%'";
      case "startswith":
        return fieldName + " LIKE '" + returnVal + "%'";
      case "endswith":
        return fieldName + " LIKE '%" + returnVal + "'";
      case "Is Greater Than":
        return nullFilterField(fieldName, fieldType) + " > " + returnVal;
      case "Is Less Than":
        return nullFilterField(fieldName, fieldType) + " < " + returnVal;
      case "Is Greater Or Equal To":
        return nullFilterField(fieldName, fieldType) + " >= " + returnVal;
      case "Is Less Or Equal To":
        return nullFilterField(fieldName, fieldType) + " <= " + returnVal;
      case "Is Empty":
        return fieldName + " is null";
      case "Is Not Empty":
        return fieldName + " is not null";
      default:
        throw new Error("operator not defined in converter method");
    }
  }
  if (fieldType === "date") {
    switch (operatorStr) {
      case "Between":
        return "DATE (" + fieldName + ")" + " BETWEEN " + `'${datesObj.start}'` + " AND " + `'${datesObj.end}'`;
      case "Not Between":
        return "DATE (" + fieldName + ")" + " NOT BETWEEN " + `'${datesObj.start}'` + " AND " + `'${datesObj.end}'`;
      case "Is Equal To":
        return "DATE (" + fieldName + ")" + " = " + returnVal;
      case "Is Not Equal To":
        return (
          "DATE (" + fieldName + ")" + " <> " + returnVal
          // +
          // " OR " +
          // nullFilterField(fieldName, fieldType) +
          // " = '' " +
          // " OR " +
          // nullFilterField(fieldName, fieldType) +
          // " = '0000-00-00 00:00:00'"
        );
      case "Is Greater Than":
        return "DATE (" + fieldName + ")" + " > " + returnVal;
      case "Is Less Than":
        return (
          "DATE (" + fieldName + ")" + " < " + returnVal
          // +
          // " AND " +
          // nullFilterField(fieldName, fieldType) +
          // " != '' " +
          // " AND " +
          // nullFilterField(fieldName, fieldType) +
          // " <> '0000-00-00 00:00:00'"

          // nullFilterField(
          //     fieldName,
          //     "DATE (" + fieldName + ")" + " = "
          // ) +
          // " < " +
          // returnVal
        );
      case "Is Greater Or Equal To":
        return "DATE (" + fieldName + ")" + " >= " + returnVal;
      case "Is Less Or Equal To":
        return (
          "DATE (" + fieldName + ")" + " <= " + returnVal
          //  +
          // " OR " +
          // nullFilterField(fieldName, fieldType) +
          // " = '' " +
          // " OR " +
          // nullFilterField(fieldName, fieldType) +
          // " = '0000-00-00 00:00:00'"
        );
      case "Is Empty":
        return (
          nullFilterField(fieldName, fieldType) + " = '' "
          //+
          // " OR " +
          // nullFilterField(fieldName, fieldType) +
          // " = '0000-00-00 00:00:00'"
        );
      case "Is Not Empty":
        return (
          nullFilterField(fieldName, fieldType) + " != '' "
          // +
          // " AND " +
          // nullFilterField(fieldName, fieldType) +
          // " <> '0000-00-00 00:00:00'"
        );
      default:
        throw new Error("operator not defined in converter method");
    }
  }
};

export const getQueryBuilderfilterDataString = (filterGroup, gridColumns, module, { prefixTopLogic = true } = {}) => {
  const root = Array.isArray(filterGroup) ? filterGroup[0] : filterGroup;
  if (!root) return "";

  const buildGroup = (group) => {
    if (!group || !Array.isArray(group.filters) || group.filters.length === 0) return "";

    const parentLogic = (group.logic || "AND").toUpperCase();
    const tokens = [];

    for (const item of group.filters) {
      // nested group
      if (item && Array.isArray(item.filters)) {
        const nestedStr = buildGroup(item);
        if (!nestedStr) continue;
        const piece = `(${nestedStr})`;

        if (tokens.length === 0) {
          tokens.push(piece);
        } else {
          // use the child's logic if present, otherwise parent's logic
          const opBefore = (item.logic || parentLogic).toUpperCase();
          tokens.push(`${opBefore} ${piece}`);
        }

        continue;
      }
      // single condition
      if (!item || !item.field) continue;
      const originalColumnObj = gridColumns.find((o) => o.field === item.field);
      if (!originalColumnObj) {
        console.warn("[getQueryBuilderfilterDataString] missing column for field:", item.field);
        continue;
      }

      const columnObj = { ...originalColumnObj };
      if (module === "event" && ["t", "d", "c", "e"].includes(columnObj.alias)) columnObj.alias = "";
      if (module === "provider-position") columnObj.alias = "";
      const field = item.dataItemKey
        ? columnObj.alias
          ? `${columnObj.alias}.${item.dataItemKey}`
          : item.dataItemKey
        : columnObj.alias
          ? `${columnObj.alias}.${item.field}`
          : item.field;
      const condition = convertFilterClauseForQueryBuilder(
        field,
        item.operator,
        item.value,
        columnObj.type,
        columnObj.dropDownControl
      );
      if (!condition) continue;

      if (tokens.length === 0) {
        tokens.push(condition);
      } else {
        // non-group items use parent's logic to connect
        tokens.push(`${parentLogic} ${condition}`);
      }
    }

    return tokens.join(" ");
  };

  const inner = buildGroup(root);
  if (!inner) return "";

  const topLogic = (root.logic || "AND").toUpperCase();
  return prefixTopLogic ? `${topLogic} ( ${inner} )` : inner;
};

export const getDefaultExpressionData = (gridColumns, operatorList, field, operator, fieldConfigMap) => {
  // Find the column you want
  const compData = gridColumns.find((x) => x.field === field);
  if (!compData) return;

  // Get operator list for that field type
  const operatorListData = getOperatorList(compData.type, operatorList, compData);
  // Column-level defaultOperator (operator_value, e.g. "IN") wins over the type's first-operator fallback.
  let selectedOperator = null;
  if (operatorListData.length > 0) {
    if (compData.defaultOperator) {
      selectedOperator = operatorListData.find((x) => x.operator_value === compData.defaultOperator);
    }
    if (!selectedOperator) {
      selectedOperator = operatorListData.find((x) => x.operator_label === operator) || operatorListData[0];
    }
  }
  // Prepare default item
  const newItems = [
    {
      id: Date.now() + Math.random(),
      value: compData,
      operator: selectedOperator,
      inputValue: {
        field: compData.field,
        value: getDefaultValueForField(
          compData.type,
          fieldConfigMap?.[compData.field]?.dataItemKey || "",
          selectedOperator?.operator_value ?? null,
          compData.dropDownControl
        ),
      },
    },
  ];

  // Wrap in group object
  const newItemData = {
    id: Date.now() + Math.random(),
    items: newItems,
    logic: "and",
    value: { logic: "and" },
  };
  return newItemData;
};

export const isSourceReqData = (data) => {
  if (!Array.isArray(data) || data.length === 0) return null;

  const filtered = data.filter((x) => x.text !== "Select All");

  if (filtered.length === 0) return null;

  return filtered
    .map((item) => {
      const value = item?.text ?? item;

      // remove existing quotes if present
      const clean = String(value).replace(/^"+|"+$/g, "");

      return `"${clean}"`;
    })
    .join(",");
};

export const inSourceFilterValue = (data) => {
  if (!Array.isArray(data) || data.length === 0) return 0;

  const filtered = data.filter((x) => x.text !== "Select All");

  if (filtered.length === 0) return 0;

  return filtered.map((item) => `"${item.text || item}"`).toString();
};

export const getEventTopFilters = (topFilters, gridColumns) => {
  const topFiltersArray = gridColumns.reduce((acc, item) => {
    let value = null;
    let operatorValue = "=";
    let operator = "In";
    let field = item.field;
    let dataItemKey = "";
    const requiredKeys = [
      "alias",
      "dataItemKey",
      "dropDownControl",
      "field",
      "gname",
      "operator",
      "operatorValue",
      "type",
    ];

    switch (item.field) {
      case "compensation_date":
        if (topFilters.in_period_start_date || topFilters.in_period_end_date) {
          value = {
            start: convertDate(topFilters.in_period_start_date),
            end: convertDate(topFilters.in_period_end_date),
          };

          operator = "Between";
          operatorValue = "BETWEEN";
        }
        break;
      case "event_data_source":
        if (topFilters.event_data_source && topFilters.event_data_source.length > 0) {
          let arr = Array.isArray(topFilters.event_data_source)
            ? topFilters.event_data_source
            : topFilters.event_data_source.split(",").map((x) => `${x.trim()}`);

          value = arr || [];
          operator = "In";
          operatorValue = "IN";
        }

        break;
      case "service_location":
        if (topFilters.service_location && topFilters.service_location.length > 0) {
          let arr = Array.isArray(topFilters.service_location)
            ? topFilters.service_location
            : topFilters.service_location.split(",").map((x) => `${x.trim()}`);

          value = arr || [];
          operator = "In";
          operatorValue = "IN";
        }
        break;
      case "position_name":
        if (topFilters.position_name && topFilters.position_name.length > 0) {
          dataItemKey = "org_position_key";
          value = topFilters.position_name;
          operator = "In";
          operatorValue = "IN";
        }
        break;
      case "comp_plan_name":
        if (topFilters.comp_plan_name && topFilters.comp_plan_name.length > 0) {
          dataItemKey = "comp_plan_mstr_key";
          value = topFilters.comp_plan_name;
          operator = "In";
          operatorValue = "IN";
        }
        break;
      default:
        break;
    }

    if (value !== null && value !== undefined) {
      const filteredItem = Object.fromEntries(Object.entries(item).filter(([key]) => requiredKeys.includes(key)));

      acc.push({
        ...filteredItem,
        value,
        operator,
        operatorValue,
        dataItemKey,
      });
    }

    return acc;
  }, []);
  let obj = {
    filters: topFiltersArray,
    logic: "AND",
  };

  return obj;
};

export const toLocalDate = (dateStr) => {
  if (!dateStr) return null;

  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
};

export const getTopFilters = (tFilter) => {
  const dataList = tFilter?.filters || tFilter?.items || [];
  const topFiltersObj = {
    in_period_start_date: null,
    in_period_end_date: null,
    event_data_source: "",
    service_location: "",
  };
  dataList.forEach((item) => {
    switch (item.field) {
      case "compensation_date":
        let startDate = item?.value?.start
          ? toLocalDate(item.value.start)
          : item?.inputValue?.value?.start
            ? toLocalDate(item.inputValue.value.start)
            : null;
        let endDate = item?.value?.end
          ? toLocalDate(item.value.end)
          : item?.inputValue?.value?.end
            ? toLocalDate(item.inputValue.value.end)
            : null;
        topFiltersObj.in_period_start_date = startDate;
        topFiltersObj.in_period_end_date = endDate;
        break;

      case "event_data_source":
        topFiltersObj.event_data_source = item?.inputValue?.value
          ? inSourceFilterValue(item?.inputValue?.value)
          : item.value;
        break;

      case "service_location":
        topFiltersObj.service_location = item?.inputValue?.value
          ? inSourceFilterValue(item?.inputValue?.value)
          : item.value;
        break;
      case "position_name":
        topFiltersObj.position_name = item?.inputValue?.value ? item?.inputValue?.value : item.value;
        break;
      case "comp_plan_name":
        topFiltersObj.comp_plan_name = item?.inputValue?.value ? item?.inputValue?.value : item.value;
        break;

      default:
        break;
    }
  });
  return topFiltersObj;
};

export const getOperatorListValue = (type, operatorList) => {
  if (!operatorList) return [];

  const map = {
    string: operatorList.stringOperator || [],
    boolean: operatorList.stringOperator || [],
    number: operatorList.numericOperator || [],
    date: operatorList.dateOperator || [],
  };

  let operators = map[type];
  return operators[0]?.operator_label || "";
};

export const sortOperators = (operators) => {
  const order = ["=", "!=", "LIKE", "NOT LIKE", "IN", "NOT IN", "IS NULL", "IS NOT NULL"];

  return [...operators].sort((a, b) => order.indexOf(a.operator_value) - order.indexOf(b.operator_value));
};

export const startDateInputSettings = {
  spinners: false,
  label: "Period Start date *",
  format: "yyyy-MM-dd",
  placeholder: "yyyy-mm-dd",
};

export const endDateInputSettings = {
  spinners: false,
  label: "Period End Date *",
  format: "yyyy-MM-dd",
  placeholder: "yyyy-mm-dd",
};

export const isArrayOfObjects = (arr) =>
  Array.isArray(arr) && arr.every((item) => typeof item === "object" && item !== null);

export const getAllUsedFields = (items = []) => {
  const fields = [];

  items.forEach((item) => {
    if (item?.items) {
      // nested group
      fields.push(...getAllUsedFields(item.items));
    } else if (item?.inputValue.field) {
      fields.push(item.inputValue.field);
    }
  });

  return fields;
};
